import { Box, Stack } from '@mui/material';
import { Button as PortalsButton } from '@portals/core/src/components/Button/Button';
import { RedirectBox, RedirectBoxProps } from '@portals/core/src/components/RedirectBox/RedirectBox';
import { Typography } from '@portals/core/src/components/Typography/Typography';
import { ThemedBox } from '@portals/core/src/containers/ThemedBox/ThemedBox';
import { createTheme as createDarkTheme } from '@portals/core/src/themes/sde/dark';
import { slugify } from '@portals/core/src/utilities/slugify';
import React, { forwardRef, RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { DefaultSparkasseAssignmentForm } from '../../forms/DefaultSparkasseAssignmentForm/DefaultSparkasseAssignmentForm';
import { useSPKCookie } from '../../hooks/useSPKCookie/useSPKCookie';

export interface SparkasseAssignmentFormState {
  showForm: boolean;
  showRedirect: boolean;
  setShowForm: (show: boolean) => void;
  setShowRedirect: (show: boolean) => void;
}

export interface SparkasseAssignmentFormProps {
  headline: string;
  subline?: string;
  RedirectBoxProps?: RedirectBoxProps;
  AssignmentFormProps?: {
    headline: string;
    subline: string;
  };
  Button?: React.ReactElement;
  ButtonProps?: {
    text: string;
    href?: string;
  };
  children?: React.ReactElement | ((props: SparkasseAssignmentFormState) => React.ReactElement);
}

export const SparkasseAssignment = forwardRef<RefObject<HTMLDivElement>, SparkasseAssignmentFormProps>(
  ({ AssignmentFormProps, headline, subline, children, RedirectBoxProps, Button, ButtonProps }, ref) => {
    const { bankCode, bankName } = useSPKCookie();
    const [showForm, setShowForm] = useState(false);
    const [showRedirect, setShowRedirect] = useState(false);
    const hiddenLinkRef = useRef<HTMLButtonElement>(null); // IF safe (no location.href manipulation)
    const [href, setHref] = useState<string | null>(ButtonProps?.href);

    const onBackClick = useCallback(() => {
      setShowForm(false);
      setShowRedirect(false);
    }, []);

    const onBankSelected = useCallback(() => setShowRedirect(true), []);

    const handleRedirect = useCallback(() => {
      if (hiddenLinkRef.current) {
        hiddenLinkRef.current.click();
      }
    }, []);

    const onButtonClick = useCallback(
      (event) => {
        event.preventDefault();
        if (!bankCode) {
          setShowForm(true);
        } else {
          setShowRedirect(true);
        }
      },
      [bankCode]
    );

    useEffect(() => {
      if (bankCode && ButtonProps?.href) {
        const url = new URL(ButtonProps.href);
        url.searchParams.set('blz', bankCode);
        setHref(url.toString());
      }
      if (!bankCode) {
        setHref(null);
      }
    }, [ButtonProps?.href, bankCode]);

    const renderForm = () => {
      if (showRedirect) {
        return (
          <RedirectBox
            {...RedirectBoxProps}
            bankName={bankName}
            redirect={RedirectBoxProps?.redirect || handleRedirect}
          />
        );
      }

      if (showForm) {
        return (
          <DefaultSparkasseAssignmentForm {...AssignmentFormProps} onSelectBank={onBankSelected} onBack={onBackClick} />
        );
      }

      return (
        <Box display="flex" flexDirection="column" gap={{ xs: 8, lg: 9 }}>
          <Stack direction="column" gap={6} px={5}>
            {headline && (
              <Typography variant="h3" color="text.primary">
                {headline}
              </Typography>
            )}
            {subline && (
              <Typography variant="body2" color="text.secondary">
                {subline}
              </Typography>
            )}
          </Stack>
          {!Button && (
            <PortalsButton onClick={onButtonClick} href={href}>
              {ButtonProps?.text}
            </PortalsButton>
          )}
          {Button && Button}
          {typeof children === 'function'
            ? children({
                showForm,
                showRedirect,
                setShowForm,
                setShowRedirect,
              })
            : children}
        </Box>
      );
    };

    return (
      <Box ref={ref} id={slugify(headline)}>
        <ThemedBox createLightTheme={createDarkTheme} createDarkTheme={createDarkTheme}>
          {renderForm()}
        </ThemedBox>
        {!!href && <Box ref={hiddenLinkRef} component="a" href={href} style={{ display: 'none' }} />}
      </Box>
    );
  }
);

SparkasseAssignment.displayName = 'SparkasseAssignment';
